import React, { useState, useEffect } from 'react';
import { useAuth0Context } from '../contexts/Auth0Context';
import axios from 'axios';

const SystemStatus = () => {
  const { getAccessToken, isAuthenticated } = useAuth0Context();
  const [systemStatus, setSystemStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testingLLM, setTestingLLM] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

  const fetchSystemStatus = async () => {
    if (!isAuthenticated) return;

    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_URL}/health/system-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });

      if (response.data.success) {
        setSystemStatus(response.data.data);
        setError(null);
      } else {
        setError('Failed to fetch system status');
      }
    } catch (err) {
      console.error('System status fetch error:', err);
      setError('Unable to fetch system status');
    } finally {
      setLoading(false);
    }
  };

  const testLLMConnection = async () => {
    if (!isAuthenticated) return;
    
    setTestingLLM(true);
    try {
      const token = await getAccessToken();
      const response = await axios.post(`${API_URL}/chat/message`, {
        message: 'test connection',
        context: { user: 'system', role: 'admin' }
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });

      if (response.data.response) {
        alert('✅ LLM Connection Test: SUCCESS\n\nAI responded successfully!');
      } else {
        alert('❌ LLM Connection Test: FAILED\n\nNo AI response received.');
      }
    } catch (err) {
      alert(`❌ LLM Connection Test: FAILED\n\nError: ${err.message}`);
    } finally {
      setTestingLLM(false);
    }
  };

  useEffect(() => {
    fetchSystemStatus();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchSystemStatus, 30000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const getStatusColor = (status) => {
    return status === 'online' ? '#10B981' : '#EF4444'; // green or red
  };

  const getStatusIcon = (status) => {
    return status === 'online' ? '🟢' : '🔴';
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">System Status</h3>
        <button
          onClick={fetchSystemStatus}
          disabled={loading}
          className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
        >
          {loading ? '🔄' : '🔃'} Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {Object.entries(systemStatus).map(([service, info]) => (
          <div key={service} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center space-x-3">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: getStatusColor(info.status) }}
                title={`${service}: ${info.status}`}
              />
              <span className="text-sm font-medium text-gray-700">{service}</span>
              {/* Enhanced details for External LLM Connection */}
              {service === 'External LLM Connection' && (
                <div className="ml-2">
                  <span className="text-xs text-gray-500">
                    {info.status === 'online' ? '🤖 AI Ready' : '❌ No AI Response'}
                  </span>
                  <button
                    onClick={testLLMConnection}
                    disabled={testingLLM}
                    className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                    title="Test LLM connection directly"
                  >
                    {testingLLM ? '🔄 Testing...' : '🧪 Test'}
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-lg">{getStatusIcon(info.status)}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                info.status === 'online' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {info.status.toUpperCase()}
              </span>
            </div>
          </div>
        ))}
      </div>

      {Object.keys(systemStatus).length === 0 && !loading && !error && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500">No system status data available</p>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center">
          Last updated: {new Date().toLocaleTimeString()} • Updates every 30s
        </p>
      </div>
    </div>
  );
};

export default SystemStatus; 